
// Serviço para gerenciar a impressão de recibos

/**
 * Interface para os formatos de impressão
 */
interface PrintFormat {
  paperType: 'A4' | 'THERMAL';
  header?: string;
  footer?: string;
  cutAtEnd?: boolean;
}

/**
 * Interface para a requisição de impressão
 */
interface PrintRequest {
  printerName?: string;
  content: string;
  format: PrintFormat;
  filename?: string;
}

/**
 * Interface para informações da impressora
 */
interface Printer {
  name: string;
  isDefault: boolean;
  status: string;
}

/**
 * Interface for PDF print request
 */
interface PdfPrintRequest {
  printerName?: string;
  pdfBase64: string;
  filename: string;
}

const DEFAULT_PRINT_TIMEOUT_MS = 15000;

// Helper function to format currency
const formatCurrency = (value: number): string => {
  return value.toFixed(2);
};

// Helper function to get payment method name
const getPaymentMethodName = (id: number | string): string => {
  // Simple mapping for common payment methods
  const paymentMethods: Record<string, string> = {
    '1': 'DINHEIRO',
    '2': 'CARTÃO DE CRÉDITO',
    '3': 'CARTÃO DE DÉBITO',
    '4': 'PIX',
    '5': 'TRANSFERÊNCIA'
  };

  return paymentMethods[id.toString()] || 'OUTROS';
};

/**
 * Function to print PDF from base64 string
 */
export const printPdfFromBase64 = async (request: PdfPrintRequest): Promise<boolean> => {
  try {
    const baseURL = getPrintServiceBaseURL();
    
    // Get configured printer if not specified
    const printerName = await resolvePrinterName(request.printerName);
    
    const printRequest = {
      printerName,
      pdfBase64: request.pdfBase64,
      filename: request.filename
    };
    
    const response = await fetch(`${baseURL}/print-pdf`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(printRequest)
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Erro no serviço de impressão: ${response.status} - ${errorText}`);
    }
    
    const result = await response.json();
    console.log("Resposta do serviço de impressão PDF:", result);
    
    return true;
  } catch (error) {
    console.error("Erro ao imprimir PDF:", error);
    if (error instanceof Error) throw error;
    throw new Error("Não foi possível imprimir o PDF. Verifique o serviço de impressão.");
  }
};

/**
 * Function to print cashier closing report
 */
export const printCashierClosingReport = async (
  cashierData: any,
  salesData: any,
  paymentsData: any
): Promise<boolean> => {
  try {
    const reportContent = formatCashierClosingReport(cashierData, salesData, paymentsData);
    
    return await printA4Document(
      reportContent,
      `Fechamento_Caixa_${new Date().toISOString().split('T')[0]}`
    );
  } catch (error) {
    console.error("Erro ao imprimir relatório de fechamento:", error);
    throw new Error("Não foi possível imprimir o relatório de fechamento.");
  }
};

/**
 * Função para obter a URL base do serviço de impressão
 */
const getPrintServiceBaseURL = (): string => {
  // Verificar se existe configuração de servidor por IP
  const serverIp = localStorage.getItem('print_server_ip');
  
  if (serverIp) {
    // Se tem IP configurado, usar ele para formar a URL
    // Verificar se já tem porta incluída
    if (serverIp.includes(':')) {
      return `http://${serverIp}`;
    } else {
      // Adicionar a porta padrão 3000
      return `http://${serverIp}:3000`;
    }
  }
  
  // Se não tem IP específico, usar a URL direta do localStorage ou o padrão
  return localStorage.getItem('print_service_url') || 'http://localhost:3000';
};

/**
 * Função para obter o nome da impressora configurada
 */
const getConfiguredPrinterName = (): string | undefined => {
  return localStorage.getItem('selected_printer') || undefined;
};

const resolvePrinterName = async (printerName?: string): Promise<string> => {
  const explicit = printerName?.trim();
  if (explicit) return explicit;

  const configured = getConfiguredPrinterName()?.trim();
  if (configured) return configured;

  const printers = await getPrinters();
  if (!Array.isArray(printers) || printers.length === 0) {
    throw new Error('Nenhuma impressora encontrada no serviço de impressão.');
  }

  const defaultOk = printers.find(p => p.isDefault && String(p.status || '').toLowerCase() !== 'error');
  const defaultAny = printers.find(p => p.isDefault);
  const chosen = defaultOk || defaultAny || printers[0];

  localStorage.setItem('selected_printer', chosen.name);
  return chosen.name;
};

/**
 * Função para formatar texto em colunas para impressão térmica
 * com suporte a quebra de linha para textos longos
 */
const formatColumns = (columns: string[], widths: number[], align: ('left' | 'right' | 'center')[] = []): string => {
  let result = '';
  for (let i = 0; i < columns.length; i++) {
    const col = columns[i] || '';
    const width = widths[i] || 10;
    const alignment = align[i] || 'left';
    
    let formattedText = '';
    if (alignment === 'right') {
      formattedText = col.padStart(width);
    } else if (alignment === 'center') {
      const padding = Math.max(0, Math.floor((width - col.length) / 2));
      formattedText = ' '.repeat(padding) + col.padEnd(width - padding);
    } else {
      formattedText = col.padEnd(width);
    }
    
    result += formattedText.substring(0, width);
  }
  return result;
  
};

/**
 * Função para quebrar texto em linhas com tamanho máximo
 */
const wrapText = (text: string, maxLength: number): string[] => {
  if (!text) return [''];
  if (text.length <= maxLength) return [text];
  
  const lines: string[] = [];
  let currentLine = '';
  const words = text.split(' ');
  
  for (const word of words) {
    if ((currentLine + ' ' + word).length <= maxLength) {
      currentLine += (currentLine ? ' ' : '') + word;
    } else {
      if (currentLine) lines.push(currentLine);
      if (word.length > maxLength) {
        // Se a palavra for maior que maxLength, quebrá-la
        let remainingWord = word;
        while (remainingWord.length > maxLength) {
          lines.push(remainingWord.substring(0, maxLength));
          remainingWord = remainingWord.substring(maxLength);
        }
        currentLine = remainingWord;
      } else {
        currentLine = word;
      }
    }
  }
  
  if (currentLine) lines.push(currentLine);
  return lines;
};

/**
 * Função para centralizar texto
 * Esta função é utilizada em várias partes do código
 */
const centerText = (text: string, width: number = 48): string => {
  const padding = Math.max(0, Math.floor((width - text.length) / 2));
  return ' '.repeat(padding) + text;
};

/**
 * Formata um comprovante de venda/pedido em formato cupom térmico.
 * - Usa layout semelhante a comprovantes de entrega (quando aplicável)
 * - Funciona para PDV, mesas e entregas
 */
export const formatSaleReceiptForThermalPrinting = (
  order: any,
  options?: { tableName?: string }
): string => {
  const width = 48;
  const separator = '-'.repeat(width);
  const doubleSeparator = '='.repeat(width);

  const now = new Date();
  const date = now.toLocaleDateString('pt-BR');
  const time = now.toLocaleTimeString('pt-BR');

  const companyName = order.companyName || localStorage.getItem('empresa_nome') || 'RSP Cloud App';
  const companyCnpj = localStorage.getItem('empresa_cnpj') || '';
  const companyAddress = localStorage.getItem('empresa_address') || '';
  const companyPhone = localStorage.getItem('empresa_phone') || '';

  const customer = order.customer;
  const customerName = order.customerName || customer?.name || 'CONSUMIDOR';
  const customerPhone = customer?.phone || '';
  const customerAddress = customer?.address || '';
  const customerCity = customer?.city || '';
  const customerState = customer?.state || '';

  const orderType = order.type || 'dine-in';
  const title = orderType === 'delivery'
    ? 'ENTREGA'
    : (options?.tableName ? options.tableName.toUpperCase() : 'COMPROVANTE');

  const orderPublicId = order.erpId || order.externalId || order.id || '';
  const orderShort = typeof orderPublicId === 'string' ? orderPublicId.slice(-8) : String(orderPublicId);

  // Totais
  const subtotalCalc = Array.isArray(order.items)
    ? order.items.reduce((sum: number, item: any) => sum + (Number(item?.product?.price || 0) * Number(item?.quantity || 0)), 0)
    : 0;
  const discount = Number(order.discount || 0);
  const deliveryFee = Number(order.deliveryFee || 0);
  const surcharge = Number(order.surcharge || 0);
  const total = Number(order.totalAmount || (subtotalCalc - discount + deliveryFee + surcharge) || 0);

  // Pagamento
  const payments: Array<{ name: string; value: number }> = [];
  if (Array.isArray(order.VendaFormaPagamento) && order.VendaFormaPagamento.length > 0) {
    for (const p of order.VendaFormaPagamento) {
      payments.push({
        name: String(p?.FormaPagamentoNome || 'PAGAMENTO').toUpperCase(),
        value: Number(p?.Valor || 0)
      });
    }
  } else if (order.paymentMethodName) {
    payments.push({ name: String(order.paymentMethodName).toUpperCase(), value: total });
  } else if (order.paymentMethodId) {
    payments.push({ name: getPaymentMethodName(order.paymentMethodId), value: total });
  }

  // Cabeçalho
  let receipt = `
${centerText(companyName.toUpperCase(), width)}
`;
  if (companyCnpj) receipt += `${centerText(`CNPJ: ${companyCnpj}`, width)}\n`;
  if (companyAddress) receipt += `${centerText(companyAddress, width)}\n`;
  if (companyPhone) receipt += `${centerText(`Fone: ${companyPhone}`, width)}\n`;

  receipt += `
${doubleSeparator}
${centerText(title, width)}
${doubleSeparator}

Emissao: ${date} ${time}
Pedido: ${orderShort || 'N/A'}
`;

  // Cliente/Entrega
  receipt += `
Cliente: ${customerName}
`;
  if (customerPhone) receipt += `Telefone: ${customerPhone}\n`;

  if (orderType === 'delivery') {
    if (customerAddress) receipt += `Endereco: ${customerAddress}\n`;
    if (customerCity || customerState) receipt += `${customerCity}${customerCity && customerState ? ' - ' : ''}${customerState}\n`;
  }

  if (order.observations) {
    const obsLines = wrapText(String(order.observations), width - 6);
    receipt += `Obs:\n`;
    for (const line of obsLines) receipt += `  ${line}\n`;
  }

  receipt += `
${separator}
${centerText('ITENS', width)}
${separator}
`;

  // Itens
  const itemColWidth = 28;
  const qtyColWidth = 6;
  const totalColWidth = 14;
  receipt += formatColumns(['Descricao', 'Qtd', 'Valor'], [itemColWidth, qtyColWidth, totalColWidth]) + '\n';

  if (Array.isArray(order.items)) {
    for (const item of order.items) {
      const name = String(item?.product?.name || 'ITEM');
      const qty = Number(item?.quantity || 0);
      const lineTotal = Number(item?.product?.price || 0) * qty;

      const nameLines = wrapText(name, itemColWidth);
      receipt += `${nameLines[0].padEnd(itemColWidth)}${String(qty).padStart(qtyColWidth)}${formatCurrency(lineTotal).padStart(totalColWidth)}\n`;
      for (let i = 1; i < nameLines.length; i++) {
        receipt += `${nameLines[i].padEnd(itemColWidth)}${''.padStart(qtyColWidth)}${''.padStart(totalColWidth)}\n`;
      }
      if (item?.notes) {
        const notesLines = wrapText(String(item.notes), width - 6);
        for (const line of notesLines) receipt += `  * ${line}\n`;
      }
    }
  }

  // Totais
  const valueColWidth = 28;
  receipt += `
${separator}
Subtotal: ${formatCurrency(subtotalCalc).padStart(valueColWidth, ' ')}
`;
  if (deliveryFee || orderType === 'delivery') {
    receipt += `Taxa entrega(+): ${formatCurrency(deliveryFee).padStart(valueColWidth - 3, ' ')}\n`;
  }
  if (surcharge) {
    receipt += `Acrescimo(+): ${formatCurrency(surcharge).padStart(valueColWidth - 1, ' ')}\n`;
  }
  if (discount) {
    receipt += `Desconto(-): ${formatCurrency(discount).padStart(valueColWidth - 1, ' ')}\n`;
  }
  receipt += `TOTAL: ${formatCurrency(total).padStart(valueColWidth + 3, ' ')}
`;

  // Pagamento
  if (payments.length > 0) {
    receipt += `
${separator}
Forma de pagamento
`;
    for (const p of payments) {
      receipt += `${p.name}: ${formatCurrency(p.value).padStart(valueColWidth - Math.min(p.name.length, 18), ' ')}\n`;
    }
  }

  // Rodapé
  receipt += `
${separator}
ID do pedido: ${String(orderPublicId)}
${doubleSeparator}
${centerText('ESTE NAO E UM DOCUMENTO FISCAL', width)}
${doubleSeparator}


`;

  return receipt;
};

/**
 * Formata um "pedido completo" (API ObterPedidoCompleto) para cupom térmico.
 * Observação: impressoras térmicas via texto não renderizam barcode; imprimimos o ID do pedido.
 */
export const formatPedidoCompletoForThermalPrinting = (
  pedido: any,
  options?: { tableNumber?: number | string }
): string => {
  const width = 48;
  const separator = '-'.repeat(width);
  const doubleSeparator = '='.repeat(width);

  const vendaId = pedido?.venda?.id ?? '';
  const vendaDataIso = pedido?.venda?.data;
  const vendaDate = vendaDataIso ? new Date(vendaDataIso) : new Date();
  const date = vendaDate.toLocaleDateString('pt-BR');
  const time = vendaDate.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

  const empresaNome =
    pedido?.empresa?.nomeFantasia ||
    pedido?.empresa?.razaoSocial ||
    localStorage.getItem('empresa_nome') ||
    'RSP Cloud App';
  const empresaCnpj = pedido?.empresa?.cnpj || localStorage.getItem('empresa_cnpj') || '';
  const empresaEndereco = pedido?.empresa?.endereco
    ? [
        pedido.empresa.endereco.logradouro,
        pedido.empresa.endereco.numero,
        pedido.empresa.endereco.complemento,
        pedido.empresa.endereco.bairro,
      ]
        .filter(Boolean)
        .join(', ')
    : (localStorage.getItem('empresa_address') || '');
  const empresaCidadeUf = pedido?.empresa?.endereco
    ? [pedido.empresa.endereco.municipio, pedido.empresa.endereco.uf].filter(Boolean).join(' - ')
    : '';

  const clienteNome = pedido?.cliente?.nome || 'CONSUMIDOR';
  const clienteDoc = pedido?.cliente?.documento || '';
  const clienteEndereco = pedido?.cliente?.endereco
    ? [
        pedido.cliente.endereco.logradouro,
        pedido.cliente.endereco.numero,
        pedido.cliente.endereco.complemento,
        pedido.cliente.endereco.bairro,
        pedido.cliente.endereco.municipio,
        pedido.cliente.endereco.uf,
      ]
        .filter(Boolean)
        .join(', ')
    : '';

  const itens = Array.isArray(pedido?.itens) ? pedido.itens : [];
  const pagamentos = Array.isArray(pedido?.pagamentos) ? pedido.pagamentos : [];

  const tableNumberRaw = options?.tableNumber;
  const tableNumber =
    tableNumberRaw === 0 || tableNumberRaw
      ? String(tableNumberRaw).trim()
      : '';

  const totalItens = itens.reduce((sum: number, i: any) => sum + Number(i?.totalFinal || 0), 0);
  const acrescimo = Number(pedido?.venda?.totalAcrescimo || 0);
  const desconto = Number(pedido?.venda?.totalDesconto || 0);
  const totalFinal = Number(pedido?.venda?.totalFinal || (totalItens + acrescimo - desconto) || 0);

  let receipt = `
${centerText(String(empresaNome).toUpperCase(), width)}
`;
  if (empresaCnpj) receipt += `${centerText(`CNPJ: ${empresaCnpj}`, width)}\n`;
  if (empresaEndereco) receipt += `${centerText(String(empresaEndereco), width)}\n`;
  if (empresaCidadeUf) receipt += `${centerText(String(empresaCidadeUf), width)}\n`;

  receipt += `
${doubleSeparator}
${centerText('PEDIDO', width)}
${doubleSeparator}

Emissao: ${date} ${time}
Pedido: ${String(vendaId) || 'N/A'}

${tableNumber ? centerText(`COMANDA/MESA: ${tableNumber}`, width) : ''}

Cliente: ${clienteNome}${clienteDoc ? ` - ${clienteDoc}` : ''}
`;

  if (clienteEndereco) {
    const addrLines = wrapText(String(clienteEndereco), width);
    for (const line of addrLines) receipt += `${line}\n`;
  }

  receipt += `
${separator}
${centerText('ITENS', width)}
${separator}
`;

  const itemColWidth = 28;
  const qtyColWidth = 6;
  const totalColWidth = 14;
  receipt += formatColumns(['Descricao', 'Qtd', 'Valor'], [itemColWidth, qtyColWidth, totalColWidth]) + '\n';

  for (const item of itens) {
    const name = String(item?.produtoNome || 'ITEM');
    const qty = Number(item?.quantidade || 0);
    const lineTotal = Number(item?.totalFinal || 0);

    const nameLines = wrapText(name, itemColWidth);
    receipt += `${nameLines[0].padEnd(itemColWidth)}${String(qty).padStart(qtyColWidth)}${formatCurrency(lineTotal).padStart(totalColWidth)}\n`;
    for (let i = 1; i < nameLines.length; i++) {
      receipt += `${nameLines[i].padEnd(itemColWidth)}${''.padStart(qtyColWidth)}${''.padStart(totalColWidth)}\n`;
    }
  }

  const valueColWidth = 28;
  receipt += `
${separator}
Total itens (=): ${formatCurrency(totalItens).padStart(valueColWidth - 3, ' ')}
Acrescimo (+): ${formatCurrency(acrescimo).padStart(valueColWidth - 2, ' ')}
Desconto (-): ${formatCurrency(desconto).padStart(valueColWidth - 2, ' ')}
TOTAL (=): ${formatCurrency(totalFinal).padStart(valueColWidth + 3, ' ')}
`;

  if (pagamentos.length > 0) {
    receipt += `
${separator}
Forma de pagamento
`;

    for (const p of pagamentos) {
      const name = String(p?.descricao || `Forma ${p?.formaPagamentoID || ''}`).toUpperCase();
      const value = Number(p?.valor || 0);
      receipt += `${name}: ${formatCurrency(value).padStart(valueColWidth - Math.min(name.length, 18), ' ')}\n`;
    }
  }

  receipt += `
${separator}
ID do pedido: ${String(vendaId)}
${tableNumber ? `COMANDA/MESA: ${tableNumber}` : ''}
${doubleSeparator}
${centerText('ESTE NAO E UM DOCUMENTO FISCAL', width)}
${doubleSeparator}


`;

  return receipt;
};

/**
 * Função para formatar um pedido para impressão térmica no formato otimizado
 */
export const formatOrderForThermalPrinting = (order: any): string => {
  const date = new Date().toLocaleDateString('pt-BR');
  const time = new Date().toLocaleTimeString('pt-BR');
  const dateTime = `${date} ${time}`;
  
  // Define a largura para impressoras térmicas de 80mm (normalmente entre 42-48 caracteres)
  // Aumentamos para 48 caracteres para aproveitar melhor a largura do papel
  const width = 48; 
  // Largura das colunas de valores - ajustada para evitar cortes
  const valueColWidth = 36; // Variável para alinhamento de valores monetários
  const separator = '-'.repeat(width);
  const doubleSeparator = '='.repeat(width);
  
  // Informações da empresa (cabeçalho)
  const companyName = order.companyName || localStorage.getItem('empresa_nome') || 'RSP Cloud App';
  const companyCnpj = localStorage.getItem('empresa_cnpj') || '';
  const companyIE = localStorage.getItem('empresa_ie') || '';
  const companyCep = localStorage.getItem('empresa_cep') || '';
  const companyPhone = localStorage.getItem('empresa_phone') || '';
  const companyAddress = localStorage.getItem('empresa_address') || '';
  
  // Informações de quem atendeu e cliente
  const waiterName = order.waiterName || localStorage.getItem('user_name') || 'ATENDENTE';
  const customerName = order.customerName || order.customerId || 'CONSUMIDOR';
  
  console.log('Formatando impressão com:', {
    waiterName,
    customerName,
    VendaFormaPagamento: order.VendaFormaPagamento,
  });
  
  // Quebrar o endereço em duas linhas se for muito longo
  let addressLine1 = companyAddress;
  let addressLine2 = '';
  if (companyAddress && companyAddress.length > width) {
    const breakPoint = companyAddress.lastIndexOf(' ', width);
    addressLine1 = companyAddress.substring(0, breakPoint);
    addressLine2 = companyAddress.substring(breakPoint + 1);
  }
  
  // Cabeçalho do recibo com informações da empresa
  let receipt = `
${centerText(companyName.toUpperCase(), width)}
`;

  if (companyCnpj) {
    receipt += `${centerText(`CNPJ: ${companyCnpj}`, width)}\n`;
  }

  if (addressLine1) {
    receipt += `${centerText(addressLine1, width)}\n`;
  }
  
  if (addressLine2) {
    receipt += `${centerText(addressLine2, width)}\n`;
  }
  
  if (companyPhone) {
    receipt += `${centerText(`Fone: ${companyPhone}`, width)}\n`;
  }
  
  if (companyIE && companyCep) {
    receipt += `${centerText(`IE: ${companyIE}   CEP: ${companyCep}`, width)}\n`;
  }

  receipt += `
${doubleSeparator}
${centerText('CUPOM NAO FISCAL', width)}
${doubleSeparator}

Emissao: ${dateTime}
Pedido: ${order.id ? order.id.substring(0, 8) : 'N/A'}
Vendedor: ${waiterName}
Cliente: ${customerName}

${separator}
${centerText('ITENS DO PEDIDO', width)}
${separator}
`;

  // Ajustar larguras de coluna para bobina de 80mm - aumentamos as larguras
  const itemColWidth = 24; // Aumentada para descrição mais clara
  const qtyColWidth = 6;
  const unitColWidth = 8; // Aumentada para valor unitário
  const totalColWidth = 10; // Aumentada para valor total

  // Cabeçalho das colunas com larguras ajustadas
  const colHeader = formatColumns(
    ["ITEM", "QTD", "VALOR", "TOTAL"], 
    [itemColWidth, qtyColWidth, unitColWidth, totalColWidth]
  );
  receipt += colHeader + "\n";
  
  // Adicionar itens do pedido em formato tabular bem organizado
  if (order.items && order.items.length > 0) {
    order.items.forEach((item: any) => {
      // Quebrar descrição em linhas se necessário
      const descriptionLines = wrapText(item.product.name, itemColWidth);
      
      // Primeira linha com todos os valores
      const quantity = item.quantity.toString().padStart(qtyColWidth, ' ');
      const price = formatCurrency(item.product.price).padStart(unitColWidth, ' ');
      const total = formatCurrency(item.quantity * item.product.price).padStart(totalColWidth, ' ');
      
      receipt += formatColumns(
        [descriptionLines[0], quantity, price, total],
        [itemColWidth, qtyColWidth, unitColWidth, totalColWidth],
        ['left', 'right', 'right', 'right']
      ) + '\n';
      
      // Linhas adicionais da descrição (se houver)
      for (let i = 1; i < descriptionLines.length; i++) {
        receipt += formatColumns(
          [descriptionLines[i], '', '', ''],
          [itemColWidth, qtyColWidth, unitColWidth, totalColWidth],
          ['left', 'right', 'right', 'right']
        ) + '\n';
      }
      
      // Adicionar observações se existirem
      if (item.notes) {
        const noteLines = wrapText(`OBS: ${item.notes}`, width - 4);
        noteLines.forEach(line => {
          receipt += `    ${line}\n`;
        });
      }
    });
  }

  // Calcular subtotal e formatar valores monetários
  const subtotal = order.items.reduce((sum: number, item: any) => sum + (item.product.price * item.quantity), 0);
  const discountAmount = order.discount ? 
    (order.discountType === 'percentage' ? (subtotal * order.discount) / 100 : order.discount) : 0;
  const total = order.totalAmount;
  
  // Rodapé com subtotal, desconto, total e formas de pagamento
  receipt += `
${separator}
SUBTOTAL:${formatCurrency(subtotal).padStart(valueColWidth, ' ')}`;

  if (order.discount && order.discount > 0) {
    const discountInfo = order.discountType === 'percentage' 
      ? `${order.discount}% - ${formatCurrency(discountAmount)}`
      : formatCurrency(order.discount);
    receipt += `\nDESCONTO:${discountInfo.padStart(valueColWidth, ' ')}`;
  }

  receipt += `\nTOTAL:   ${formatCurrency(total).padStart(valueColWidth - 2, ' ')}

${separator}
${centerText('PAGAMENTO', width)}
${separator}`;

  // Exibir formas de pagamento detalhadas
  let displayedPayments = false;

  // Verificamos primeiro se temos VendaFormaPagamento no objeto
  if (order.VendaFormaPagamento && order.VendaFormaPagamento.length > 0) {
    order.VendaFormaPagamento.forEach((payment: any) => {
      const paymentLines = wrapText(payment.FormaPagamentoNome, valueColWidth - 10);
      receipt += `\n${paymentLines[0]}:${formatCurrency(payment.Valor).padStart(valueColWidth - paymentLines[0].length, ' ')}`;
      
      // Se houver mais linhas na descrição do pagamento
      for (let i = 1; i < paymentLines.length; i++) {
        receipt += `\n${paymentLines[i]}`;
      }
    });
    displayedPayments = true;
  }
  // Depois verificamos se temos pagamentos detalhados com paymentMethods e paymentValues
  else if (order.paymentMethods && order.paymentMethods.length > 0 && order.paymentValues && order.paymentValues.length > 0) {
    // Se temos múltiplas formas de pagamento
    order.paymentMethods.forEach((method: any, index: number) => {
      const methodName = method.name || getPaymentMethodName(method.id);
      const value = order.paymentValues[index] || 0;
      receipt += `\n${methodName}:${formatCurrency(value).padStart(valueColWidth, ' ')}`;
    });
    displayedPayments = true;
  }
  // Depois verificamos se há um pagamento detalhado em payment
  else if (order.payment && order.payment.method) {
    receipt += `\n${order.payment.method}:${formatCurrency(order.payment.amount || order.totalAmount).padStart(valueColWidth, ' ')}`;
    displayedPayments = true;
  }
  // Por último, verificamos o método de pagamento simples
  else if (order.paymentMethodId) {
    const paymentMethod = getPaymentMethodName(order.paymentMethodId);
    receipt += `\n${paymentMethod}:${formatCurrency(total).padStart(valueColWidth, ' ')}`;
    displayedPayments = true;
  }
  
  // Se nenhuma forma de pagamento foi exibida, usar DINHEIRO como padrão
  if (!displayedPayments) {
    receipt += `\nDINHEIRO:${formatCurrency(total).padStart(valueColWidth, ' ')}`;
  }

  // Informações de valor pago e troco
  const receivedAmount = order.receivedAmount || (order.paymentValues ? order.paymentValues.reduce((sum: number, value: number) => sum + value, 0) : total);
  const changeAmount = order.changeAmount || (receivedAmount > total ? receivedAmount - total : 0);

  if (receivedAmount) {
    receipt += `\nVALOR PAGO:${formatCurrency(receivedAmount).padStart(valueColWidth, ' ')}`;
  }
  
  if (changeAmount > 0) {
    receipt += `\nTROCO:${formatCurrency(changeAmount).padStart(valueColWidth + 6, ' ')}`;
  }

  receipt += `

${doubleSeparator}
${centerText('SEM VALOR FISCAL', width)}
${centerText('Obrigado pela preferencia!', width)}
${doubleSeparator}


`;

  return receipt;
};

/**
 * Função para formatar um pré-fechamento de mesa para impressão térmica
 */
export const formatTablePreClosing = (order: any, tableName: string): string => {
  const date = new Date().toLocaleDateString('pt-BR');
  const time = new Date().toLocaleTimeString('pt-BR');
  const dateTime = `${date} ${time}`;
  
  const companyName = localStorage.getItem('company_name') || 'RSP TECNOLOGIA';
  
  // Define a largura para impressoras térmicas de 80mm
  const width = 42;
  const separator = '-'.repeat(width);
  const doubleSeparator = '='.repeat(width);
  
  // Cabeçalho do pré-fechamento com título específico para mesa
  const tableTitle = `Fechamento MESA - ${tableName}`;
  
  let receipt = `
${centerText(companyName.toUpperCase(), width)}

${doubleSeparator}
${centerText(tableTitle, width)}
${doubleSeparator}

Emissao: ${dateTime}
Pedido: ${order.id ? order.id.slice(-6) : 'N/A'}
Atendente: ${order.waiterName || 'ATENDENTE'}

${separator}
${centerText('CONSUMO DA MESA', width)}
${separator}
`;

  // Ajustar larguras de coluna para bobina de 80mm
  const itemColWidth = 22;
  const qtyColWidth = 6;
  const unitColWidth = 7;
  const totalColWidth = 7;

  // Cabeçalho das colunas com larguras ajustadas
  const colHeader = formatColumns(
    ["ITEM", "QTD", "VALOR", "TOTAL"], 
    [itemColWidth, qtyColWidth, unitColWidth, totalColWidth]
  );
  receipt += colHeader + "\n";
  
  // Adicionar itens do pedido em formato tabular
  if (order.items && order.items.length > 0) {
    order.items.forEach((item: any) => {
      // Limita a descrição para caber na coluna
      let description = item.product.name.substring(0, itemColWidth);
      description = description.padEnd(itemColWidth);
      
      const quantity = item.quantity.toString().padStart(qtyColWidth, ' ');
      const price = formatCurrency(item.product.price).padStart(unitColWidth, ' ');
      const total = formatCurrency(item.quantity * item.product.price).padStart(totalColWidth, ' ');
      
      receipt += `${description}${quantity}${price}${total}\n`;
    });
  }

  // Rodapé com total
  const total = formatCurrency(order.totalAmount || 0);
  const discount = formatCurrency(order.discount || 0);
  
  // Largura das colunas de valores - ajustada para evitar cortes
  const valueColWidth = 30;
  
  receipt += `
${separator}
SUBTOTAL:${total.padStart(valueColWidth, ' ')}
DESCONTO:${discount.padStart(valueColWidth, ' ')}
TOTAL:   ${total.padStart(valueColWidth - 2, ' ')}

${doubleSeparator}
${centerText('ESTE NAO E UM DOCUMENTO FISCAL', width)}
${doubleSeparator}


`;

  return receipt;
};

/**
 * Função que formata um pedido para impressão térmica (formato antigo)
 */
export const formatOrderForPrinting = (order: any, tableName?: string) => {
  const date = new Date().toLocaleDateString('pt-BR');
  const time = new Date().toLocaleTimeString('pt-BR');
  const width = 42;
  const separator = '-'.repeat(width);
  const doubleSeparator = '='.repeat(width);
  
  let receipt = `
${centerText('RECIBO DE PEDIDO', width)}

${doubleSeparator}
Data: ${date}
Hora: ${time}
${tableName ? `Mesa: ${tableName}` : 'Pedido para viagem/entrega'}
Pedido: #${order.id.slice(-4)}
${doubleSeparator}
${centerText('ITENS DO PEDIDO', width)}
${separator}
`;

  // Larguras das colunas
  const itemColWidth = 22;
  const qtyColWidth = 6;
  const unitColWidth = 7;
  const totalColWidth = 7;

  // Cabeçalho das colunas com larguras ajustadas
  receipt += formatColumns(
    ["ITEM", "QTD", "VALOR", "TOTAL"], 
    [itemColWidth, qtyColWidth, unitColWidth, totalColWidth]
  ) + "\n";

  // Adicionar itens do pedido
  order.items.forEach((item: any) => {
    let description = item.product.name.substring(0, itemColWidth);
    description = description.padEnd(itemColWidth);
    
    const quantity = item.quantity.toString().padStart(qtyColWidth, ' ');
    const price = formatCurrency(item.product.price).padStart(unitColWidth, ' ');
    const total = formatCurrency(item.quantity * item.product.price).padStart(totalColWidth, ' ');
    
    receipt += `${description}${quantity}${price}${total}\n`;
    
    if (item.notes) {
      receipt += `   Obs: ${item.notes}\n`;
    }
  });

  // Rodapé com total
  receipt += `
${separator}
TOTAL: R$ ${order.totalAmount.toFixed(2)}
${doubleSeparator}
${centerText('Obrigado pela preferência!', width)}


`;

  return receipt;
};

/**
 * Função para converter HTML para PDF em formato Base64
 * Esta função converte conteúdo HTML em um PDF codificado em Base64
 */
export const convertHtmlToPdfBase64 = async (htmlContent: string): Promise<string> => {
  try {
    const baseURL = getPrintServiceBaseURL();
    console.log("Convertendo HTML para PDF via serviço:", baseURL);
    
    const response = await fetch(`${baseURL}/html-to-pdf`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ html: htmlContent })
    });
    
    if (!response.ok) {
      throw new Error(`Erro na conversão HTML para PDF: ${response.status}`);
    }
    
    const result = await response.json();
    return result.pdfBase64;
  } catch (error) {
    console.error("Erro ao converter HTML para PDF:", error);
    throw new Error("Não foi possível converter o HTML para PDF.");
  }
};

/**
 * Função para obter a lista de impressoras disponíveis
 */
export const getPrinters = async (): Promise<Printer[]> => {
  try {
    const baseURL = getPrintServiceBaseURL();
    console.log("Tentando conectar ao serviço de impressão em:", baseURL);
    
    const response = await fetch(`${baseURL}/printers`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      signal: AbortSignal.timeout(5000)
    });
    
    if (!response.ok) {
      throw new Error(`Erro ao obter impressoras: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error("Erro ao consultar impressoras:", error);
    throw new Error("Não foi possível obter a lista de impressoras disponíveis.");
  }
};

/**
 * Função que envia um trabalho de impressão para o serviço local
 */
export const printReceipt = async (receipt: string, printerName?: string): Promise<boolean> => {
  try {
    console.log("Enviando para o serviço de impressão:");
    
    const baseURL = getPrintServiceBaseURL();
    console.log("Usando URL do serviço:", baseURL);
    
    const configuredPrinter = await resolvePrinterName(printerName);
    
    // Validar que temos os dados necessários
    if (!receipt) {
      throw new Error("Conteúdo da impressão não pode estar vazio");
    }
    
    if (!configuredPrinter) throw new Error("Nome da impressora não configurado.");
    
    // Preparar a requisição conforme a documentação da API
    const printRequest: PrintRequest = {
      printerName: configuredPrinter, // Usa a impressora informada ou a configurada
      content: receipt,
      format: {
        paperType: 'THERMAL',
        cutAtEnd: true
      }
    };
    
    console.log("Enviando requisição para o serviço de impressão:", JSON.stringify(printRequest));
    
    // Tentativa de conexão com o serviço de impressão local
    const response = await fetch(`${baseURL}/print`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(printRequest),
      signal: AbortSignal.timeout(DEFAULT_PRINT_TIMEOUT_MS)
    });
    
    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(`Erro no serviço de impressão: ${response.status}${errorText ? ` - ${errorText}` : ''}`);
    }
    
    const contentType = response.headers.get('content-type') || '';
    const result = contentType.includes('application/json')
      ? await response.json()
      : await response.text();
    console.log("Resposta do serviço de impressão:", result);
    
    return true;
  } catch (error) {
    console.error("Erro ao imprimir:", error);
    if (error instanceof Error) throw error;
    throw new Error("Não foi possível imprimir. Verifique o serviço de impressão.");
  }
};

/**
 * Função para verificar se o serviço de impressão está disponível
 */
export const checkPrintService = async (): Promise<boolean> => {
  try {
    const baseURL = getPrintServiceBaseURL();
    console.log("Verificando disponibilidade do serviço de impressão em:", baseURL);
    
    const response = await fetch(`${baseURL}/printers`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      // Timeout mais curto para não travar a aplicação
      signal: AbortSignal.timeout(2000)
    });
    
    return response.ok;
  } catch (error) {
    console.log("Serviço de impressão não disponível:", error);
    return false;
  }
};

/**
 * Função para impressão de documento A4
 */
export const printA4Document = async (
  content: string, 
  title: string = 'Documento', 
  printerName?: string, 
  header?: string,
  footer?: string
): Promise<boolean> => {
  try {
    console.log("Enviando documento A4 para impressão");
    
    const baseURL = getPrintServiceBaseURL();
    const configuredPrinter = printerName || getConfiguredPrinterName();

    if (localStorage.getItem('use_thermal_receipt_format') === 'false') {
      // Imprimir diretamente no navegador
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(`
          <html>
          <head>
            <title>${title}</title>
            <style>
              body { font-family: Arial, sans-serif; }
            </style>
          </head>
          <body>${content}</body>
          </html>
        `);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
        printWindow.close();
        return true;
      } else {
        throw new Error("Não foi possível abrir a janela de impressão.");
      }
    } else {
      // Preparar a requisição conforme a documentação da API
      const printRequest: PrintRequest = {
        printerName: configuredPrinter, // Usa a impressora informada ou a configurada
        content: content,
        format: {
          paperType: 'A4',
          header: header,
          footer: footer
        },
        filename: `${title.replace(/\s+/g, '_')}.pdf`
      };
      
      const response = await fetch(`${baseURL}/print`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(printRequest)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Erro na impressão A4: ${response.status} - ${errorText}`);
      }
      
      const result = await response.json();
      console.log("Resposta do serviço de impressão A4:", result);
      
      return true;
    }
  } catch (error) {
    console.error("Erro ao imprimir documento A4:", error);
    throw new Error("Não foi possível imprimir o documento A4. Verifique o serviço de impressão.");
  }
};

/**
 * Função para verificar o status de um trabalho de impressão
 */
export const checkPrintJobStatus = async (jobId: string): Promise<any> => {
  try {
    const baseURL = getPrintServiceBaseURL();
    const response = await fetch(`${baseURL}/jobs/${jobId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });
    
    if (!response.ok) {
      throw new Error(`Erro ao verificar status do trabalho: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error("Erro ao verificar status do trabalho:", error);
    throw new Error("Não foi possível verificar o status do trabalho de impressão.");
  }
};

/**
 * Função para listar todos os trabalhos de impressão
 */
export const listPrintJobs = async (): Promise<any[]> => {
  try {
    const baseURL = getPrintServiceBaseURL();
    const response = await fetch(`${baseURL}/jobs`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });
    
    if (!response.ok) {
      throw new Error(`Erro ao listar trabalhos de impressão: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error("Erro ao listar trabalhos de impressão:", error);
    throw new Error("Não foi possível listar os trabalhos de impressão.");
  }
};

/**
 * Função para formatar um relatório de fechamento de caixa para impressão A4
 */
export const formatCashierClosingReport = (
  cashierData: any,
  salesData: any,
  paymentsData: any
): string => {
  const date = new Date().toLocaleDateString('pt-BR');
  const time = new Date().toLocaleTimeString('pt-BR');
  
  if (localStorage.getItem('use_thermal_receipt_format') === 'true') {
    // Format for thermal printer
    const width = 48;
    const separator = '-'.repeat(width);
    const doubleSeparator = '='.repeat(width);
    
    let receipt = `
${centerText('FECHAMENTO DE CAIXA', width)}
${doubleSeparator}

Data: ${date}
Hora: ${time}
Operador: ${cashierData.operator}

${separator}
${centerText('RESUMO DE VENDAS', width)}
${separator}
Total Pedidos: ${salesData.totalOrders}
Valor Total: ${formatCurrency(salesData.totalAmount)}
Ticket Médio: ${formatCurrency(salesData.averageTicket)}
${separator}
${centerText('FORMAS DE PAGAMENTO', width)}
${separator}
`;

    paymentsData.forEach(payment => {
      const paymentLines = wrapText(payment.name, 30);
      receipt += `${paymentLines[0]}: ${formatCurrency(payment.amount).padStart(width - paymentLines[0].length - 2, ' ')}\n`;
      
      for (let i = 1; i < paymentLines.length; i++) {
        receipt += `${paymentLines[i]}\n`;
      }
    });

    receipt += `
${separator}
TOTAL: ${formatCurrency(salesData.totalAmount).padStart(width - 7, ' ')}
${doubleSeparator}
${centerText('FECHAMENTO REALIZADO', width)}
${centerText('COM SUCESSO', width)}
${doubleSeparator}


`;

    return receipt;
  } else {
    // Format for A4 printing (browser)
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Fechamento de Caixa</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          .header { text-align: center; margin-bottom: 20px; }
          .section { margin-bottom: 20px; }
          .table { width: 100%; border-collapse: collapse; }
          .table th, .table td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          .table th { background-color: #f2f2f2; }
          .footer { margin-top: 30px; text-align: center; }
          .total { font-weight: bold; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Relatório de Fechamento de Caixa</h1>
          <p>Data: ${date} - Hora: ${time}</p>
          <p>Operador: ${cashierData.operator}</p>
        </div>
        
        <div class="section">
          <h2>Resumo de Vendas</h2>
          <table class="table">
            <tr>
              <th>Total de Pedidos</th>
              <td>${salesData.totalOrders}</td>
            </tr>
            <tr>
              <th>Valor Total</th>
              <td>${formatCurrency(salesData.totalAmount)}</td>
            </tr>
            <tr>
              <th>Ticket Médio</th>
              <td>${formatCurrency(salesData.averageTicket)}</td>
            </tr>
          </table>
        </div>
        
        <div class="section">
          <h2>Formas de Pagamento</h2>
          <table class="table">
            <tr>
              <th>Forma de Pagamento</th>
              <th>Valor</th>
            </tr>
            ${paymentsData.map(payment => `
              <tr>
                <td>${payment.name}</td>
                <td>${formatCurrency(payment.amount)}</td>
              </tr>
            `).join('')}
            <tr class="total">
              <td>TOTAL</td>
              <td>${formatCurrency(salesData.totalAmount)}</td>
            </tr>
          </table>
        </div>
        
        <div class="footer">
          <p>Fechamento realizado com sucesso</p>
          <p>${new Date().toLocaleString('pt-BR')}</p>
        </div>
      </body>
      </html>`;
  }
};
